/**
 * Browser tab titles for both SPAs (dashboard and v3 auth).
 *
 * `vueapp.html.erb` renders a single static <title>, so the tab used to read
 * `CloudChat` on every screen. Both routers now call `updateDocumentTitle` on
 * `afterEach`, and the section name is resolved in this order:
 *
 *   1. `meta.title` on the matched route records, deepest first.
 *   2. `ROUTE_TITLE_KEYS[route.name]`.
 *   3. The humanized route name — a route added later still gets a usable
 *      title without anyone touching this file.
 *
 * Values in (1) and (2) may be an i18n key, a literal string, or a
 * `(route, { t, te }) => string` function for titles built from route params.
 * Keys point at existing `SIDEBAR.*` / `*.HEADER` strings wherever one exists
 * so the tab matches the nav item or page heading the user just clicked, and
 * so pt_BR and es come along for free.
 */

const TITLE_SEPARATOR = ' | ';

// Words humanizeRouteName must not sentence-case.
const ACRONYMS = {
  api: 'API',
  crm: 'CRM',
  csat: 'CSAT',
  csml: 'CSML',
  faq: 'FAQ',
  mcp: 'MCP',
  sla: 'SLA',
  url: 'URL',
};

// Keys shared by three or more routes — a wizard's steps, a report and its
// Omni twin, the tag views. Hoisted so the key lives in one place.
const KEY_LABELS = 'SIDEBAR.LABELS';
const KEY_NEW_INBOX = 'SIDEBAR.NEW_INBOX';
const KEY_NEW_TEAM = 'SIDEBAR.NEW_TEAM';
const KEY_EDDIE_EDITOR = 'SIDEBAR.EDDIE_EDITOR';
const KEY_AGENT_REPORTS = 'AGENT_REPORTS.HEADER';
const KEY_TEAM_REPORTS = 'TEAM_REPORTS.HEADER';

// The conversation detail routes disagree on the param name — `conversation_id`
// in the inbox/label/custom_view chains, `conversationId` in the team/mentions/
// unattended/participating ones — so read both before falling back to the bare
// label. Getting this wrong renders "Conversation #undefined".
const conversationSection = (route, { t }) => {
  const params = route.params || {};
  const id = params.conversationId || params.conversation_id;
  return id
    ? t('PAGE_TITLES.CONVERSATION_WITH_ID', { id })
    : t('PAGE_TITLES.CONVERSATION');
};

export const ROUTE_TITLE_KEYS = {
  // --- conversations -------------------------------------------------------
  home: 'SIDEBAR.CONVERSATIONS',
  inbox_dashboard: 'SIDEBAR.INBOX',
  label_conversations: KEY_LABELS,
  team_conversations: 'SIDEBAR.TEAMS',
  folder_conversations: 'SIDEBAR.CUSTOM_VIEWS_FOLDER',
  conversation_mentions: 'SIDEBAR.MENTIONED_CONVERSATIONS',
  conversation_unattended: 'SIDEBAR.UNATTENDED_CONVERSATIONS',
  conversation_participating: 'SIDEBAR.PARTICIPATING_CONVERSATIONS',
  inbox_view: 'SIDEBAR.INBOX_VIEW',
  captain: 'SIDEBAR.CAPTAIN',
  inbox_conversation: conversationSection,
  conversation_through_inbox: conversationSection,
  conversations_through_label: conversationSection,
  conversations_through_team: conversationSection,
  conversations_through_folders: conversationSection,
  conversation_through_mentions: conversationSection,
  conversation_through_unattended: conversationSection,
  conversation_through_participating: conversationSection,
  inbox_view_conversation: conversationSection,

  // --- contacts, search, notifications -------------------------------------
  contacts_dashboard: 'CONTACTS_PAGE.HEADER',
  contacts_segments_dashboard: 'SIDEBAR.CUSTOM_VIEWS_SEGMENTS',
  contacts_labels_dashboard: 'SIDEBAR.TAGGED_WITH',
  contact_profile_dashboard: 'PAGE_TITLES.CONTACT_PROFILE',
  notifications_index: 'SIDEBAR.NOTIFICATIONS',
  search: 'PAGE_TITLES.SEARCH',
  account_suspended: 'PAGE_TITLES.ACCOUNT_SUSPENDED',

  // --- ClaudIA -------------------------------------------------------------
  claudia_app: 'SIDEBAR.CLAUDIA',
  claudia_default: 'SIDEBAR.CLAUDIA',
  claudia_agents: 'SIDEBAR.CLAUDIA_CONVERSATIONAL_AGENTS',
  claudia_tools: 'SIDEBAR.CLAUDIA_TOOLS',
  claudia_tools_catchall: 'SIDEBAR.CLAUDIA_TOOLS',
  claudia_mcp: 'SIDEBAR.CLAUDIA_MCP_SERVERS',
  claudia_settings: 'SIDEBAR.CLAUDIA_SETTINGS',
  claudia_deploy: 'SIDEBAR.CLAUDIA_DEPLOY',
  claudia_create_project: 'SIDEBAR.CLAUDIA_CREATE_PROJECT',
  claudia_controlled_flows: 'SIDEBAR.CLAUDIA_CONTROLLED_FLOWS',
  claudia_controlled_flows_catchall: 'SIDEBAR.CLAUDIA_CONTROLLED_FLOWS',
  knowledge_base_view: 'SIDEBAR.KNOWLEDGE_BASE',
  knowledge_base_view_catchall: 'SIDEBAR.KNOWLEDGE_BASE',
  improvement_center_view: 'SIDEBAR.CLAUDIA_IMPROVEMENT_CENTER',
  improvement_center_view_catchall: 'SIDEBAR.CLAUDIA_IMPROVEMENT_CENTER',

  // --- Eddie (flow editor) -------------------------------------------------
  eddie_app: KEY_EDDIE_EDITOR,
  eddie_view: KEY_EDDIE_EDITOR,
  eddie_view_legacy: KEY_EDDIE_EDITOR,

  // --- help center ---------------------------------------------------------
  default_portal_articles: 'SIDEBAR.HELP_CENTER.TITLE',
  list_all_portals: 'PAGE_TITLES.PORTALS',
  new_portal_information: 'PAGE_TITLES.NEW_PORTAL',
  portal_customization: 'PAGE_TITLES.PORTAL_CUSTOMIZATION',
  portal_finish: 'PAGE_TITLES.NEW_PORTAL',
  portalSlug: 'SIDEBAR.HELP_CENTER.TITLE',
  edit_portal_information: 'PAGE_TITLES.PORTAL_SETTINGS',
  edit_portal_customization: 'PAGE_TITLES.PORTAL_CUSTOMIZATION',
  edit_portal_locales: 'PAGE_TITLES.PORTAL_LOCALES',
  list_all_locale_articles: 'SIDEBAR.HELP_CENTER.ALL_ARTICLES',
  list_mine_articles: 'SIDEBAR.HELP_CENTER.MY_ARTICLES',
  list_archived_articles: 'PAGE_TITLES.ARCHIVED_ARTICLES',
  list_draft_articles: 'PAGE_TITLES.DRAFT_ARTICLES',
  new_article: 'PAGE_TITLES.NEW_ARTICLE',
  edit_article: 'PAGE_TITLES.EDIT_ARTICLE',
  all_locale_categories: 'PAGE_TITLES.CATEGORIES',
  list_all_locale_categories: 'PAGE_TITLES.CATEGORIES',
  new_category_in_locale: 'PAGE_TITLES.NEW_CATEGORY',
  show_category: 'SIDEBAR.HELP_CENTER.CATEGORY',
  show_category_articles: 'SIDEBAR.HELP_CENTER.CATEGORY',
  edit_category: 'PAGE_TITLES.EDIT_CATEGORY',

  // --- settings ------------------------------------------------------------
  settings_home: 'SIDEBAR.SETTINGS',
  settings_onboarding_guide: 'SIDEBAR.ONBOARDING_GUIDE_NAV',
  general_settings_index: 'SIDEBAR.ACCOUNT_SETTINGS',
  profile_settings: 'SIDEBAR.PROFILE_SETTINGS',
  profile_settings_index: 'SIDEBAR.PROFILE_SETTINGS',
  agent_list: 'SIDEBAR.AGENTS',
  agent_bots: 'AGENT_BOTS.HEADER',
  agent_bots_csml_new: 'PAGE_TITLES.NEW_BOT',
  agent_bots_csml_edit: 'PAGE_TITLES.EDIT_BOT',
  custom_attributes_list: 'SIDEBAR.CUSTOM_ATTRIBUTES',
  native_attributes_list: 'SIDEBAR.NATIVE_ATTRIBUTES',
  auditlogs_list: 'SIDEBAR.AUDIT_LOGS',
  automation_list: 'SIDEBAR.AUTOMATION',
  availability_list: 'SIDEBAR.CUSTOM_AVAILABILITY',
  billing_settings_index: 'SIDEBAR.BILLING',
  canned_list: 'SIDEBAR.CANNED_RESPONSES',
  custom_roles_list: 'SIDEBAR.CUSTOM_ROLES',
  labels_list: KEY_LABELS,
  labels_wrapper: KEY_LABELS,
  labels_legacy_list: KEY_LABELS,
  labels_legacy_wrapper: KEY_LABELS,
  macros_wrapper: 'MACROS.HEADER',
  macros_new: 'PAGE_TITLES.NEW_MACRO',
  macros_edit: 'PAGE_TITLES.EDIT_MACRO',
  sla_list: 'SIDEBAR.SLA',
  sla_wrapper: 'SIDEBAR.SLA',

  // --- settings: inboxes ---------------------------------------------------
  settings_inbox_list: 'INBOX_MGMT.HEADER',
  settings_inbox_new: KEY_NEW_INBOX,
  settings_inboxes_page_channel: KEY_NEW_INBOX,
  settings_inboxes_add_agents: KEY_NEW_INBOX,
  settings_inbox_finish: KEY_NEW_INBOX,
  settings_inbox_show: 'PAGE_TITLES.INBOX_SETTINGS',

  // --- settings: teams -----------------------------------------------------
  settings_teams_list: 'TEAMS_SETTINGS.HEADER',
  settings_teams_new: KEY_NEW_TEAM,
  settings_teams_add_agents: KEY_NEW_TEAM,
  settings_teams_finish: KEY_NEW_TEAM,
  settings_teams_edit: 'PAGE_TITLES.EDIT_TEAM',

  // --- settings: integrations ---------------------------------------------
  settings_integrations: 'INTEGRATION_SETTINGS.HEADER',
  settings_applications: 'SIDEBAR.APPLICATIONS',
  settings_applications_integration: 'SIDEBAR.INTEGRATION',
  settings_integrations_dashboard_apps: 'PAGE_TITLES.DASHBOARD_APPS',
  settings_integrations_webhook: 'PAGE_TITLES.WEBHOOKS',
  settings_integrations_slack: 'PAGE_TITLES.SLACK',

  // --- campaigns -----------------------------------------------------------
  one_off: 'SIDEBAR.ONE_OFF',
  ongoing_campaigns: 'SIDEBAR.ONGOING',
  campaigns_suppressions: 'SIDEBAR.EMAIL_CAMPAIGNS_SUPPRESSIONS',
  email_campaigns_wrapper: 'SIDEBAR.CAMPAIGNS',
  email_campaigns_domains: 'SIDEBAR.EMAIL_CAMPAIGNS_DOMAINS',
  email_campaigns_domain_show: 'SIDEBAR.EMAIL_CAMPAIGNS_DOMAINS',
  email_campaigns_senders: 'SIDEBAR.EMAIL_CAMPAIGNS_SENDERS',
  email_campaigns_postal_address: 'SIDEBAR.EMAIL_CAMPAIGNS_POSTAL_ADDRESS',

  // --- reports -------------------------------------------------------------
  account_overview_reports: 'OVERVIEW_REPORTS.HEADER',
  advanced_monitoring: 'ADVANCED_MONITORING.HEADER',
  conversation_reports: 'REPORT.HEADER',
  omni_conversations_report: 'REPORT.HEADER',
  service_time_metrics_reports: 'SERVICE_TIME_METRICS_REPORTS.HEADER',
  omni_service_time_metrics_report: 'SERVICE_TIME_METRICS_REPORTS.HEADER',
  campaign_metrics_reports: 'CAMPAIGN_METRICS_REPORTS.HEADER',
  omni_campaign_metrics_report: 'CAMPAIGN_METRICS_REPORTS.HEADER',
  omni_claudia_general_info_report: 'CLAUDIA_GENERAL_INFO_REPORTS.HEADER',
  claudia_metricas_legado_report: 'CLAUDIA_METRICAS_LEGADO_REPORTS.HEADER',
  csat_reports: 'CSAT_REPORTS.HEADER',
  omni_csat_report: 'CSAT_REPORTS.HEADER',
  bot_reports: 'BOT_REPORTS.HEADER',
  agent_reports: KEY_AGENT_REPORTS,
  omni_agents_report: KEY_AGENT_REPORTS,
  label_reports: 'LABEL_REPORTS.HEADER',
  inbox_reports: 'INBOX_REPORTS.HEADER',
  team_reports: KEY_TEAM_REPORTS,
  omni_teams_report: KEY_TEAM_REPORTS,
  ticket_metrics_reports: 'TICKET_METRICS_REPORTS.HEADER',
  sla_reports: 'SLA_REPORTS.HEADER',
  omni_sla_policies_report: 'SLA_POLICIES_REPORTS.HEADER',
  omni_claudia_controlled_flows_report:
    'CLAUDIA_CONTROLLED_FLOWS_REPORTS.HEADER',
  reports_team_view: KEY_TEAM_REPORTS,
  reports_agent_view: KEY_AGENT_REPORTS,
  reports_report_builder: 'REPORT_BUILDER.HEADER',
  reports_create_mode: 'CREATE_MODE.HEADER',

  // --- v3 auth app ---------------------------------------------------------
  login: 'PAGE_TITLES.LOGIN',
  auth_signup: 'REGISTER.TITLE',
  auth_confirmation: 'PAGE_TITLES.CONFIRMATION',
  auth_reset_password: 'RESET_PASSWORD.TITLE',
  auth_password_edit: 'SET_NEW_PASSWORD.TITLE',
};

/**
 * `agent_list` -> `Agent list`, `csat_reports` -> `CSAT reports`.
 *
 * Sentence case rather than title case: it degrades more gracefully on the
 * longer route names (`settings_inboxes_add_agents`).
 */
export const humanizeRouteName = (routeName = '') => {
  const words = String(routeName)
    // camelCase route names exist too (`portalSlug`).
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .split(/[\s_\-/]+/)
    .filter(Boolean);

  return words
    .map((word, index) => {
      const acronym = ACRONYMS[word.toLowerCase()];
      if (acronym) return acronym;
      if (index === 0) {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }
      return word.toLowerCase();
    })
    .join(' ');
};

/**
 * Turns a map/meta value into a display string. Functions get the route so
 * they can read params; strings are translated when they resolve to a known
 * i18n key and used verbatim otherwise.
 */
const resolveTitleValue = (value, route, i18n) => {
  if (typeof value === 'function') return value(route, i18n) || '';
  if (typeof value !== 'string' || !value) return '';

  const { t, te } = i18n;
  // te() only inspects the active locale, so a key that lives only in the `en`
  // fallback would otherwise be reported as missing on pt_BR and es.
  if (te(value) || te(value, 'en')) return t(value);
  return value;
};

// vue-router exposes only the deepest matched record on `route.meta`, so walk
// `matched` in reverse to let a child override its parent while still honouring
// a title declared on an ancestor.
const findMetaTitle = route => {
  const matched = route.matched || [];
  for (let i = matched.length - 1; i >= 0; i -= 1) {
    const { title } = matched[i].meta || {};
    if (title) return title;
  }
  return undefined;
};

export const resolveRouteSection = (route, i18n) => {
  if (!route) return '';

  const metaTitle = findMetaTitle(route);
  if (metaTitle) {
    const resolved = resolveTitleValue(metaTitle, route, i18n);
    if (resolved) return resolved;
  }

  if (!route.name) return '';

  const mapped = ROUTE_TITLE_KEYS[route.name];
  if (mapped) {
    const resolved = resolveTitleValue(mapped, route, i18n);
    if (resolved) return resolved;
  }

  return humanizeRouteName(route.name);
};

export const buildDocumentTitle = (section = '', installationName = '') => {
  if (!section) return installationName;
  if (!installationName) return section;
  // Guard against `Settings | CloudChat` becoming `CloudChat | CloudChat` when
  // a section resolves to the brand itself.
  if (section === installationName) return installationName;
  return `${section}${TITLE_SEPARATOR}${installationName}`;
};

/**
 * Writes the tab title for `route`. Never throws: a broken title must not take
 * a navigation down with it.
 */
export const updateDocumentTitle = (route, { i18n, store }) => {
  try {
    const section = resolveRouteSection(route, {
      t: (key, values) => i18n.t(key, values),
      te: (key, locale) => i18n.te(key, locale),
    });
    const { installationName } = store.getters['globalConfig/get'] || {};
    const title = buildDocumentTitle(section, installationName);
    if (title) document.title = title;
    return title;
  } catch (error) {
    // Deliberately not rethrown: the title is cosmetic and must never take a
    // navigation down with it. Logged so a broken meta.title still surfaces
    // instead of failing silently.
    // eslint-disable-next-line no-console
    console.error('[pageTitle] could not resolve the document title', error);
    return document.title;
  }
};

export default updateDocumentTitle;
